import { EventEmitter } from "@prof-dev/event-emitter";

declare global {
  interface Window {
    CustomModal: typeof CustomModal;
  }
}

type ModalsMapping = Map<string, CustomModal>;

const customModalsMapping: ModalsMapping = new Map<string, CustomModal>();

export class CustomModal extends EventEmitter<"open" | "close", CustomModal> {
  private toggleElements: HTMLElement[] = [];
  private closeElements = new Set<HTMLElement>();
  public readonly modalElement: HTMLElement | null;
  private modalBackfaceElement: HTMLElement | null = null;

  private constructor(private name: string) {
    super();

    this.modalElement = document.querySelector(
      `[data-custom-modal="${this.name}"]`,
    );

    this.modalBackfaceElement =
      this.modalElement?.querySelector(".modal-backface") ?? null;

    this.init();
    this.initClose();
    this.initToggles();

    customModalsMapping.set(this.name, this);
  }

  private init = () => {
    this.toggleElements.forEach((toggleElement) => {
      toggleElement.addEventListener("click", this.onOpenHandler);
    });

    this.modalBackfaceElement?.addEventListener("click", this.onCloseHandler);
  };

  initToggles = () => {
    this.toggleElements = Array.from(
      document.querySelectorAll(
        `[data-toggle="custom-modal"][data-target="${this.name}"]`,
      ),
    );

    this.toggleElements.forEach((toggleElement) => {
      toggleElement.removeEventListener("click", this.onOpenHandler);
      toggleElement.addEventListener("click", this.onOpenHandler);
    });
  };

  initClose = () => {
    if (this.modalElement) {
      const closeElements = Array.from(
        this.modalElement.querySelectorAll<HTMLElement>(
          `[data-modal-action="close"]`,
        ),
      );

      closeElements.forEach((closeToggle) => {
        if (this.closeElements.has(closeToggle) === false) {
          this.closeElements.add(closeToggle);

          closeToggle.addEventListener("click", this.onCloseHandler);
        }
      });
    }
  };

  private onOpenHandler = (event: Event) => {
    event.preventDefault();
    this.open();
  };

  private onCloseHandler = (event: Event) => {
    event.preventDefault();
    this.close();
  };

  toggle = (force?: boolean) => {
    if (this.modalElement) {
      const isActive = this.modalElement.classList.toggle("is-active", force);

      if (isActive === true) {
        this.modalElement.focus();
      }

      document.body.classList.toggle("no-scroll", isActive);
    }
  };

  open = () => {
    customModalsMapping.forEach((modal) => {
      modal.toggle(false);
    });

    this.toggle(true);
    this.dispatch("open", this);
  };

  close = () => {
    this.toggle(false);
    this.dispatch("close", this);
  };

  destroy = () => {
    this.toggleElements.forEach((toggleElement) => {
      toggleElement.removeEventListener("click", this.onOpenHandler);
    });

    this.modalBackfaceElement?.removeEventListener(
      "click",
      this.onCloseHandler,
    );

    this.closeElements.forEach((closeToggle) => {
      closeToggle.removeEventListener("click", this.onCloseHandler);
    });

    this.toggleElements = [];
    this.closeElements.clear();
    this.modalBackfaceElement = null;

    customModalsMapping.delete(this.name);
  };

  static open = (name: string) => {
    const instance = customModalsMapping.get(name);

    if (instance !== undefined) {
      instance.open();
    }
  };

  static close = (name: string) => {
    const instance = customModalsMapping.get(name);

    if (instance !== undefined) {
      instance.close();
    }
  };

  static destroy = (name: string) => {
    customModalsMapping.get(name)?.destroy();
  };

  static getInstance = (
    name: string,
  ):
    | {
        name: string;
        modal: CustomModal;
      }
    | undefined => {
    const instance = customModalsMapping.get(name);

    if (instance !== undefined) {
      return {
        name,
        modal: instance,
      };
    }

    return undefined;
  };

  static create = (name: string): CustomModal => {
    const instance = customModalsMapping.get(name);

    if (instance !== undefined) {
      instance.initClose();
      instance.initToggles();

      return instance;
    }

    return new CustomModal(name);
  };
}
